import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ReceiptText } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const day = (iso) => (iso ? new Date(/[Z+]/.test(iso.slice(19)) ? iso : `${iso}Z`).toLocaleDateString() : "—");
const money = (r) => (r.amount != null ? `$${r.amount.toFixed(2)}` : "—");

const ActionDialog = ({ req, mode, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const refund = mode === "refund";
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/refund-requests/${req.id}/${mode}`, refund ? {} : { note });
      toast.success(refund ? `Refunded ${money(req)} to ${req.email}` : "Request declined");
      onDone();
    } catch (e) { toast.error(errMsg(e, refund ? "Refund failed" : "Could not decline")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid={`refund-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>{refund ? `Refund ${money(req)}?` : "Decline this request?"}</DialogTitle>
          <DialogDescription className="[overflow-wrap:anywhere]">
            {refund ? `This refunds ${req.email} in full through ${req.method || "the original method"}, removes their paid access and emails them that it's done.`
              : `${req.email} will get an email saying the request wasn't approved.`}
          </DialogDescription>
        </DialogHeader>
        {!refund && <textarea className="mm-input min-h-[80px]" maxLength={500} placeholder="Optional note to the member" value={note} onChange={(e) => setNote(e.target.value)} data-testid="refund-decline-note" />}
        <div className="flex justify-end gap-2">
          <button className="mm-btn-ghost !py-2 !px-4 text-sm" disabled={busy} onClick={onClose} data-testid={`refund-${mode}-cancel`}>Cancel</button>
          <button className={`mm-btn !py-2 !px-4 text-sm inline-flex items-center gap-2 ${refund ? "" : "!bg-mm-error"}`} disabled={busy} onClick={go} data-testid={`refund-${mode}-confirm`}>
            {busy && <Loader2 size={14} className="animate-spin" />} {refund ? "Refund now" : "Decline"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ r, onAct }) => (
  <div className="mm-soft p-3 text-sm" data-testid={`refund-request-${r.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-semibold truncate">{r.name || "—"} · <span className="font-normal text-mm-muted">{r.email}</span></div>
        <div className="text-xs text-mm-muted">{r.receipt_id || "—"} · {money(r)} · {r.method || "—"} · bought {day(r.date)} · requested {day(r.requested_at)}</div>
      </div>
      {onAct ? (
        <div className="flex gap-2 shrink-0">
          <button className="mm-btn !py-1.5 !px-3 text-xs" disabled={r.status === "processing"} onClick={() => onAct(r, "refund")} data-testid={`refund-now-${r.id}`}>Refund now</button>
          <button className="mm-btn-ghost !py-1.5 !px-3 text-xs" disabled={r.status === "processing"} onClick={() => onAct(r, "decline")} data-testid={`refund-decline-${r.id}`}>Decline</button>
        </div>
      ) : (
        <span className={`mm-chip !cursor-default !text-[11px] !py-0.5 shrink-0 ${r.status === "refunded" ? "mm-chip-on" : ""}`} data-testid={`refund-status-${r.id}`}>
          {r.status === "refunded" ? "Refunded" : "Declined"} · {day(r.handled_at)}
        </span>
      )}
    </div>
    {r.reason && <p className="text-xs mt-1.5">“{r.reason}”</p>}
    {!onAct && r.note && <p className="text-xs mt-1 text-mm-muted">Note: {r.note}</p>}
  </div>
);

export const RefundRequests = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [act, setAct] = useState(null);
  const load = () => api.get("/admin/refund-requests").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  return (
    <div className="mm-card p-5 mt-6" data-testid="admin-refund-requests">
      <div className="flex items-center gap-2 font-semibold">
        <ReceiptText size={18} className="text-mm-primary" /> Refund requests
        <span className={`rounded-full px-2 py-0.5 text-xs ${data.pending.length ? "bg-mm-error text-white" : "mm-soft !rounded-full text-mm-muted"}`} data-testid="refund-pending-count">{data.pending.length}</span>
      </div>
      <div className="space-y-2 mt-3">
        {data.pending.length ? data.pending.map((r) => <Row key={r.id} r={r} onAct={(req, mode) => setAct({ req, mode })} />)
          : <p className="text-sm text-mm-muted" data-testid="refund-pending-empty">No pending refund requests.</p>}
      </div>
      {data.handled.length > 0 && (
        <div className="mt-5 pt-4 border-t" data-testid="refund-handled">
          <div className="text-xs font-semibold text-mm-muted mb-2">Recently handled</div>
          <div className="space-y-2">{data.handled.map((r) => <Row key={r.id} r={r} />)}</div>
        </div>
      )}
      {act && <ActionDialog req={act.req} mode={act.mode} onClose={() => setAct(null)} onDone={() => { setAct(null); load(); onChange?.(); }} />}
    </div>
  );
};
